import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {cloneDeep, isEmpty} from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";

import {
  createUpdateEventRule,
  requestContentTypes,
  requestEventWebhooks,
} from "../../../redux/Actions/eventRule.actions";
import {
  getContentTypesList,
  getEventFieldTypesList,
  getEventWebhooksList,
} from "../../../redux/Selectors/eventRule.selector";

import {filterScopeBySitesAndLocations} from "../../../utils/filterScope";
import {extractSitesAndLocationsFromScope} from "../../../utils/extractSitesAndLocationsFromScope";
import {useScopeAndAppliances} from "../../../hooks/useScopeAndAppliances";

import {
  DEFAULT_FORM_DATA,
  MODE_FIELDS,
  MODE_FIELD_EMPTY_VALUES,
  describeJsonError,
  filterEventTypeOptions,
  getConditionsServerError,
  getEventRuleMode,
} from "./constants";

export const useFormData = (data = cloneDeep(DEFAULT_FORM_DATA), callback) => {
  const dispatch = useDispatch();

  const webhookOptions = useSelector(getEventWebhooksList);
  const contentTypeOptions = useSelector(getContentTypesList);

  // Event field types are fetched once by the Event Rules page.
  const allEventTypeOptions = useSelector(getEventFieldTypesList);

  const {applianceOptions, scopeOptions, fetchScope, fetchAppliances} =
    useScopeAndAppliances();

  const [formData, setFormData] = useState(data);
  const [conditionsError, setConditionsError] = useState("");

  // Ignore responses from submissions that are no longer current.
  const submitId = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const {event_types = [], appliances = [], id} = formData;

  const mode = getEventRuleMode(event_types);

  // Single definition shared by submit logic and rendering.
  const isActive = useCallback((field) => mode.fields.includes(field), [mode]);

  const eventTypeOptions = useMemo(
    () => filterEventTypeOptions(event_types, allEventTypeOptions),
    [event_types, allEventTypeOptions]
  );

  useEffect(() => {
    // Always refetch: webhooks can change between visits.
    dispatch(requestEventWebhooks());

    if (isEmpty(contentTypeOptions)) {
      dispatch(requestContentTypes());
    }

    const scopeCallback = (scopeResults) => {
      if (!id) {
        return;
      }

      const calculatedScope = filterScopeBySitesAndLocations(
        scopeResults,
        formData.sites,
        formData.locations
      );

      setFormData((prevData) => ({
        ...prevData,
        scope: calculatedScope,
      }));

      fetchAppliances(calculatedScope);
    };

    // Always fetch fresh scope because the shared general.scope cache
    // can be stale.
    fetchScope(scopeCallback);

    // Intentionally run on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((e) => {
    const {name: fieldName, value} = e.target;

    // Invalidate an in-flight submission when relevant fields change.
    if (fieldName === "conditions" || fieldName === "event_types") {
      submitId.current += 1;
      setConditionsError("");
    }

    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  }, []);

  const handleToggleEnabled = useCallback(() => {
    setFormData((prevData) => ({
      ...prevData,
      enabled: !prevData.enabled,
    }));
  }, []);

  const handleScopeChange = useCallback(
    (value) => {
      const scopeChangeCallback = (newOptions) => {
        const newAppliances = newOptions.filter((item) =>
          appliances.includes(item.value)
        );

        setFormData((prevData) => ({
          ...prevData,
          appliances: newAppliances.map((item) => item.value),
        }));
      };

      setFormData((prevData) => ({
        ...prevData,
        scope: value,
      }));

      fetchAppliances(value, scopeChangeCallback);
    },
    [appliances, fetchAppliances]
  );

  const handleSubmit = useCallback(() => {
    const {name, webhook, conditions, scope: currentScope} = formData;
    submitId.current += 1;
    const thisSubmit = submitId.current;

    const missingBaseFields = isEmpty(name) || isEmpty(event_types) || !webhook;
    const missingModeFields = mode.required.some((field) => isEmpty(formData[field]));

    if (missingBaseFields || missingModeFields) {
      toast.error("Please check required fields");
      return;
    }

    let parsedConditions = MODE_FIELD_EMPTY_VALUES.conditions;

    if (isActive("conditions") && !isEmpty(conditions)) {
      try {
        parsedConditions = JSON.parse(conditions);
      } catch (error) {
        setConditionsError(describeJsonError(error));
        toast.error("Please check the conditions");
        return;
      }
    }

    // Reset API fields that do not belong to the current mode.
    // Scope is UI-only; the API receives sites and locations.
    const inactiveFieldValues = MODE_FIELDS.filter(
      (field) => field !== "scope" && !isActive(field)
    ).reduce((acc, field) => ({...acc, [field]: MODE_FIELD_EMPTY_VALUES[field]}), {});

    const {sites, locations} = isActive("scope")
      ? extractSitesAndLocationsFromScope(currentScope)
      : {sites: [], locations: []};

    // Remove the UI-only scope property before sending the payload.
    // eslint-disable-next-line no-unused-vars
    const {scope: _scope, ...rest} = formData;

    dispatch(
      createUpdateEventRule(
        {
          ...rest,
          ...inactiveFieldValues,
          sites,
          locations,
          conditions: parsedConditions,
        },
        callback,
        (error) => {
          if (!isMounted.current || thisSubmit !== submitId.current) {
            return;
          }
          const message = getConditionsServerError(error);
          if (message) {
            setConditionsError(message);
          }
        }
      )
    );
  }, [callback, dispatch, event_types, formData, isActive, mode]);

  return {
    applianceOptions,
    conditionsError,
    contentTypeOptions,
    eventTypeOptions,
    formData,
    handleChange,
    handleScopeChange,
    handleSubmit,
    handleToggleEnabled,
    isActive,
    mode,
    scopeOptions,
    webhookOptions,
  };
};
